import Button from '~/common/components/Button';
import CrudGrid from '~/common/components/CrudGrid';
import { OPERATORS } from '~/common/components/CrudGrid/Filters/filterGridData';
import type { GridDef } from '~/common/components/CrudGrid/GridDef';
import { useUser } from '~/common/hooks/useAuthentication';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import useUrl from '~/common/hooks/useUrl';
import { formatCurrency } from '~/common/util/format';
import { formatName } from '~/pages/Loan1003DataGrids/common/utils/utils';

import {
  useDeleteAssetInformationMutation,
  useGetAssetsSummaryInformationQuery,
} from '../hooks';
import { useAsset1003Store } from '../store';
import { DialogDeleteMessage } from './DialogDeleteMessage';
import { assetColumns } from './assetColumns';

const gridDef: GridDef = {
  defaultSort: { field: 'balance', direction: 'desc' },
  uniqueIdAttributeFields: ['assetId'],
  allowFilters: true,
  gridLanguage: {
    singularItem: 'Asset',
    pluralItem: 'Assets',
  },
  getDialogDeleteMessage: (rows) => <DialogDeleteMessage list={rows} />,
};

export function AssetsCrudGrid() {
  const { data, isLoading } = useGetAssetsSummaryInformationQuery();

  const activeForm = useAsset1003Store((s) => s.activeForm);

  const { mutate: handleDelete } = useDeleteAssetInformationMutation();

  const { isReadOnly } = useUrl();

  const { isUnderwriter } = useUser();

  const { AdditionalReservesInBoltEnabled } = useNavFeatureFlags();

  const isAuditHistoryEnabled = data?.displayAuditHistory && isUnderwriter;

  const isWriteAccessDisabled = useIsWriteAccessDisabled() || isReadOnly;

  const filters = [
    {
      options: data?.filteredBorrowers?.map((borrower) => {
        return {
          value: borrower.customerRecordId,
          label: formatName(
            borrower.firstName,
            borrower.lastName,
            borrower.middleName,
            borrower.suffixName,
          ),
        };
      }),
      label: 'Borrower(s)',
      fieldName: 'borrowers',
      operator: OPERATORS.EXCLUDE,
      evaluateFilter: (row, value) =>
        row.borrowers.some((borrower) =>
          value.includes(borrower.customerRecordId),
        ),
    },
  ];

  const multiDelete = {
    buttonLabel: 'Delete Multiple Assets',
    subActions: [
      {
        label: 'Select All / Deselect All',
        action: (allData: any[], currentSelected: any[]) => {
          if (currentSelected.length === allData.length) {
            return [];
          }
          return allData;
        },
      },
    ],
  };

  return (
    <CrudGrid
      gridDef={gridDef}
      columns={assetColumns}
      data={data?.transformedAssets ?? []}
      selectedRow={
        activeForm?.type === 'asset' ? { assetId: activeForm.assetId } : null
      }
      onRowEditClick={(row) => {
        useAsset1003Store.setState({
          activeForm: { type: 'asset', assetId: row.assetId },
        });
      }}
      filters={filters}
      isLoading={isLoading}
      onDelete={(list) => handleDelete(list)}
      multiSelectActionDef={multiDelete}
      onGridModeChange={() => {
        useAsset1003Store.setState({ activeForm: null });
      }}
      readOnly={isWriteAccessDisabled}
      isAuditHistoryEnabled={isAuditHistoryEnabled}
      actions={
        <>
          {isUnderwriter && (
            <Button
              dataTestid="addSubordinateFinancing"
              onClick={() => {
                useAsset1003Store.setState({
                  activeForm: { type: 'subordinateFinancing' },
                });
              }}
            >
              Add New Subordinate Financing
            </Button>
          )}
          {AdditionalReservesInBoltEnabled && isUnderwriter && (
            <Button
              dataTestid="addReservesButton"
              onClick={() => {
                useAsset1003Store.setState({
                  activeForm: { type: 'reserves' },
                });
              }}
            >
              Add Reserves
            </Button>
          )}
          <Button
            dataTestid="addAssetButton"
            onClick={() => {
              useAsset1003Store.setState({
                activeForm: { type: 'asset', assetId: null },
              });
            }}
          >
            Add Asset
          </Button>
        </>
      }
      summary={
        <strong>Total Assets: {formatCurrency(data?.totalAssets)}</strong>
      }
    />
  );
}
